import { apiFetch } from '../utils/apiFetch'
import { executeCapability } from '../utils/capabilityExecute'
import { API_BASE_URL } from '../utils/apiBaseUrl'
import {
  DEFAULT_CERT_TYPE_DEFS,
  type CertTypeDef,
} from '../utils/certLabels'

export type { CertTypeDef } from '../utils/certLabels'
export {
  CERT_LABELS,
  certDisplayLabel,
  certLabel,
  DEFAULT_CERT_TYPE_DEFS,
} from '../utils/certLabels'

export interface ContactEntry {
  employeeId: string
  name: string
  preferredName?: string | null
  position: string
  phone: string
  email: string
  workEmail?: string | null
  workPhone?: string | null
  department?: string
  supervisor?: string
}

export interface EmployeeProfile {
  employeeId: string
  firstName?: string
  lastName?: string
  preferredName?: string
  position?: string
  department?: string
  supervisor?: string
  name?: string
  hireDate?: string | null
  driverStatus?: string | null
  driversLicenseExpirationDate?: string | null
  driversLicenseType?: string | null
  dotMedicalCardExpirationDate?: string | null
  zipCode?: string | null
  payType?: string | null
}

export type CertType = string

export interface EmployeeCert {
  type: CertType
  label?: string
  expirationDate: string | null
  status: 'good' | 'bad' | 'inProgress'
  filePath?: string | null
  uploadedAt?: string | null
}

let certTypesCache: CertTypeDef[] | null = null
let certTypesInflight: Promise<CertTypeDef[]> | null = null

export function invalidateCertTypesCache() {
  certTypesCache = null
  certTypesInflight = null
}

class ProfileService {
  private baseUrl = API_BASE_URL

  private authHeaders(): HeadersInit | undefined {
    const token = localStorage.getItem('lts_jwt_token')
    return token ? { Authorization: `Bearer ${token}` } : undefined
  }

  async getMyProfile(): Promise<EmployeeProfile> {
    return executeCapability<EmployeeProfile>({
      app: 'profile',
      capability: 'profiles.getSelf',
      input: {},
    })
  }

  /** Re-auth with password; returns hire/driver/DOT/ZIP/pay-type fields for the signed-in user. */
  async unlockSensitiveSelf(currentPassword: string): Promise<Pick<
    EmployeeProfile,
    | 'hireDate'
    | 'driverStatus'
    | 'driversLicenseExpirationDate'
    | 'driversLicenseType'
    | 'dotMedicalCardExpirationDate'
    | 'zipCode'
    | 'payType'
  >> {
    return executeCapability({
      app: 'profile',
      capability: 'profiles.unlockSensitiveSelf',
      input: { currentPassword },
    })
  }

  async getProfileByEmployeeId(employeeId: string): Promise<EmployeeProfile> {
    return executeCapability<EmployeeProfile>({
      app: 'profile',
      capability: 'profiles.getByEmployeeId',
      input: { employeeId },
    })
  }

  async searchProfiles(q: string): Promise<EmployeeProfile[]> {
    return executeCapability<EmployeeProfile[]>({
      app: 'profile',
      capability: 'profiles.search',
      input: { q },
    })
  }

  async listContacts(q?: string): Promise<ContactEntry[]> {
    return executeCapability<ContactEntry[]>({
      app: 'profile',
      capability: 'contacts.list',
      input: { q: q || '' },
    })
  }

  async getAllContacts(): Promise<ContactEntry[]> {
    return executeCapability<ContactEntry[]>({
      app: 'profile',
      capability: 'contacts.list',
      input: {},
    })
  }

  async createContact(payload: { employeeId: string; name: string; preferredName?: string; position: string; email: string; phone: string; workEmail?: string; workPhone?: string; department?: string; supervisor?: string }): Promise<ContactEntry> {
    return executeCapability<ContactEntry>({
      app: 'profile',
      capability: 'contacts.create',
      input: payload,
    })
  }

  async createProfile(payload: { employeeId: string }): Promise<EmployeeProfile> {
    return executeCapability<EmployeeProfile>({
      app: 'profile',
      capability: 'profiles.create',
      input: payload,
    })
  }

  async updateMyContact(payload: { preferredName?: string; email?: string; phone?: string; workEmail?: string; workPhone?: string }): Promise<ContactEntry> {
    // Resolve current user's employeeId via token payload on backend side requires explicit employeeId here
    const me = await this.getMyProfile().catch(() => null)
    const empId = me?.employeeId
    if (!empId) throw new Error('Missing employeeId')
    return executeCapability<ContactEntry>({
      app: 'profile',
      capability: 'contacts.updateByEmployeeId',
      input: { employeeId: empId, ...payload },
    })
  }

  async getContactByEmployeeId(employeeId: string): Promise<ContactEntry> {
    return executeCapability<ContactEntry>({
      app: 'profile',
      capability: 'contacts.getByEmployeeId',
      input: { employeeId },
    })
  }

  async updateContactByEmployeeId(employeeId: string, payload: { name?: string; preferredName?: string; position?: string; email?: string; phone?: string; workEmail?: string; workPhone?: string; department?: string; supervisor?: string }): Promise<ContactEntry> {
    return executeCapability<ContactEntry>({
      app: 'profile',
      capability: 'contacts.updateByEmployeeId',
      input: { employeeId, ...payload },
    })
  }

  async updateProfileByEmployeeId(employeeId: string, payload: {
    preferredName?: string
    firstName?: string
    lastName?: string
    position?: string
    department?: string
    supervisor?: string
    hireDate?: string | null
    driverStatus?: string | null
    driversLicenseExpirationDate?: string | null
    driversLicenseType?: string | null
    dotMedicalCardExpirationDate?: string | null
    zipCode?: string | null
    payType?: string | null
  }): Promise<EmployeeProfile> {
    return executeCapability<EmployeeProfile>({
      app: 'profile',
      capability: 'profiles.updateByEmployeeId',
      input: { employeeId, ...payload },
    })
  }

  async listCertTypes(): Promise<CertTypeDef[]> {
    if (certTypesCache) return certTypesCache
    if (certTypesInflight) return certTypesInflight
    certTypesInflight = executeCapability<{ types?: CertTypeDef[] }>({
      app: 'profile',
      capability: 'certs.listTypes',
      input: {},
    })
      .then((data) => {
        const types = Array.isArray(data?.types)
          ? data.types
              .map((item) => ({
                key: String(item?.key || '').trim(),
                label: String(item?.label || '').trim(),
              }))
              .filter((item) => item.key && item.label)
          : []
        certTypesCache = types.length ? types : [...DEFAULT_CERT_TYPE_DEFS]
        return certTypesCache
      })
      .finally(() => {
        certTypesInflight = null
      })
    return certTypesInflight
  }

  async getCertsByEmployeeId(employeeId: string): Promise<EmployeeCert[]> {
    return executeCapability<EmployeeCert[]>({
      app: 'profile',
      capability: 'certs.listByEmployeeId',
      input: { employeeId },
    })
  }

  async updateCert(employeeId: string, certType: CertType, payload: { expirationDate?: string | null; status?: 'good' | 'bad' | 'inProgress' | null }): Promise<EmployeeCert> {
    return executeCapability<EmployeeCert>({
      app: 'profile',
      capability: 'certs.update',
      input: { employeeId, certType, ...payload },
    })
  }

  async updateCerts(
    employeeId: string,
    updates: Array<{ certType: CertType; expirationDate?: string | null; status?: 'good' | 'bad' | 'inProgress' | null }>,
  ): Promise<EmployeeCert[]> {
    return executeCapability<EmployeeCert[]>({
      app: 'profile',
      capability: 'certs.updateMany',
      input: { employeeId, updates },
    })
  }

  async assignCertToEmployee(employeeId: string, certType: CertType): Promise<EmployeeCert> {
    return executeCapability<EmployeeCert>({
      app: 'profile',
      capability: 'certs.assignToEmployee',
      input: { employeeId, certType },
    })
  }

  async uploadCertFile(employeeId: string, certType: CertType, file: File): Promise<EmployeeCert> {
    const reader = new FileReader()
    const base64 = await new Promise<string>((resolve, reject) => {
      reader.onload = () => {
        const result = reader.result as string
        const base64Part = result.includes(',') ? result.split(',')[1] : result
        resolve(base64Part)
      }
      reader.onerror = () => reject(reader.error)
      reader.readAsDataURL(file)
    })
    return executeCapability<EmployeeCert>({
      app: 'profile',
      capability: 'certs.upload',
      input: { employeeId, certType, pdfBase64: base64 },
    })
  }

  async deleteCert(employeeId: string, certType: CertType): Promise<void> {
    await executeCapability({
      app: 'profile',
      capability: 'certs.delete',
      input: { employeeId, certType },
    })
  }

  getCertFileUrl(employeeId: string, certType: CertType): string {
    return `${this.baseUrl}/profiles/${encodeURIComponent(employeeId)}/certs/${certType}/file`
  }

  async getCertFileBlobUrl(employeeId: string, certType: CertType): Promise<string> {
    const url = this.getCertFileUrl(employeeId, certType)
    const res = await apiFetch(url, { headers: this.authHeaders() })
    if (!res.ok) throw new Error('Failed to load cert file')
    const blob = await res.blob()
    const contentType = res.headers.get('content-type') || blob.type || 'application/pdf'
    const typed = blob.type === contentType ? blob : new Blob([blob], { type: contentType })
    return URL.createObjectURL(typed)
  }
}

export const profileService = new ProfileService()


